import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { FilterChooserScreenProps } from 'home/stack/types'
import { cloneDeep } from 'lodash'
import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useScheduleContext } from 'schedule/schedule_context'
import Spinner from 'shared/components/spinner'
import { showHeaderButton } from 'shared/header/header_button'
import { useRecentStoresContext } from 'stores/context'
import {
  areDefaultFilters,
  getDefaultFilters,
  useFiltersContext,
} from './context'
import { BooleanFilter, Filter, Preset } from './types'
import { FilterChooserView } from './views/filters'

const PRESETS_COUNT = 3

export function FilterChooserScreen({ navigation }: FilterChooserScreenProps) {
  const { theme } = useTheme()
  const {
    filters,
    currentFilters,
    setCurrentFiltersWithToggle,
    setCurrentFilters,
    setFilters,
    savedPresets,
    setSavedPresets,
    selectedPresetIndex,
    setSelectedPresetIndex,
    currentSelectedPresetIndex,
    setCurrentSelectedPresetIndex,
    scheduleCalendar,
  } = useFiltersContext()
  const { handleNavigation } = useRoutingContext()
  const { activeStoreId } = useRecentStoresContext()
  const { schedule, setSchedule } = useScheduleContext()
  const [loading, setLoading] = useState<boolean>(false)
  const custom = !areDefaultFilters(currentFilters)
  const onCancel = useCallback(() => {
    setLoading(true)
    navigation.goBack()
  }, [navigation])

  useEffect(() => {
    setCurrentSelectedPresetIndex(selectedPresetIndex)
  }, [selectedPresetIndex, setCurrentSelectedPresetIndex])

  useEffect(() => {
    setCurrentFilters(cloneDeep(filters))
  }, [filters, setCurrentFilters])

  useEffect(() => {
    const presetCountArray = [...Array(PRESETS_COUNT).keys()]
    if (savedPresets === null) {
      setSavedPresets(
        presetCountArray.map(presetIndex => {
          return {
            id: presetIndex,
            filters: cloneDeep(filters),
          }
        })
      )
    }
  }, [filters, savedPresets, setSavedPresets])

  useLayoutEffect(
    () =>
      showHeaderButton({
        navigation,
        theme,
        title: t('cancel'),
        onPress: onCancel,
      }),
    [navigation, onCancel, theme]
  )

  const onPressFilter = useCallback(
    (filter: Filter, booleanValue?: boolean) => {
      switch (filter.type) {
        case 'boolean': {
          if (booleanValue !== undefined) {
            const newFilters = cloneDeep(currentFilters)

            const booleanFilter = newFilters[filter.name] as BooleanFilter
            booleanFilter.on = booleanValue
            setCurrentFiltersWithToggle(newFilters)
            setCurrentSelectedPresetIndex(undefined)
          }
          return
        }
        case 'singleSelect': {
          if (filter.name === 'incentives') {
            handleNavigation({ routeName: 'incentives_filter' })
          } else if (filter.name === 'startTime') {
            handleNavigation({ routeName: 'start_time_filter' })
          }
          return
        }
        case 'multiSelect':
          if (filter.name === 'crewMembers') {
            handleNavigation({ routeName: 'crew_member_filter' })
          } else if (filter.name === 'stores') {
            handleNavigation({ routeName: 'stores_filter' })
          } else {
            if (schedule?.calendar === 'helper') {
              handleNavigation({ routeName: 'open_shifts_position_filter' })
            } else {
              handleNavigation({ routeName: 'position_filter' })
            }
          }

          return
      }
    },
    [
      currentFilters,
      handleNavigation,
      schedule?.calendar,
      setCurrentFiltersWithToggle,
      setCurrentSelectedPresetIndex,
    ]
  )

  const onApplyFilters = useCallback(() => {
    setLoading(true)
    setFilters(currentFilters)
    setSelectedPresetIndex(currentSelectedPresetIndex)
    if (schedule?.calendar === 'store' && activeStoreId) {
      setSchedule({
        calendar: 'store',
        storeId: activeStoreId,
        custom: custom,
      })
      handleNavigation({ routeName: 'schedule' })
    } else {
      navigation.pop(1)
      setSchedule({
        calendar: 'helper',
        openShiftsOnly: true,
        custom: custom,
      })
      navigation.replace('schedule')
    }
  }, [
    setFilters,
    currentFilters,
    setSelectedPresetIndex,
    currentSelectedPresetIndex,
    schedule?.calendar,
    activeStoreId,
    setSchedule,
    custom,
    handleNavigation,
    navigation,
  ])

  const onReset = useCallback(() => {
    setLoading(true)
    setCurrentFiltersWithToggle(getDefaultFilters())
    setLoading(false)
    setCurrentSelectedPresetIndex(undefined)
  }, [setCurrentFiltersWithToggle, setCurrentSelectedPresetIndex])

  const onSelectPreset = useCallback(
    (selectedPreset?: Preset) => {
      setCurrentSelectedPresetIndex(selectedPreset && selectedPreset.id)
      if (!selectedPreset) {
        setCurrentFiltersWithToggle(getDefaultFilters())
        return
      }
      if (!savedPresets || selectedPreset.id >= savedPresets.length) {
        return
      }

      setCurrentFiltersWithToggle(selectedPreset.filters)
    },
    [savedPresets, setCurrentFiltersWithToggle, setCurrentSelectedPresetIndex]
  )

  const onPressSave = useCallback(() => {
    handleNavigation({ routeName: 'save_filter' })
  }, [handleNavigation])

  return loading ? (
    <Spinner />
  ) : (
    <FilterChooserView
      currentFilters={currentFilters}
      filters={filters}
      onPressFilter={onPressFilter}
      onApplyFilters={onApplyFilters}
      onSelectPreset={onSelectPreset}
      savedPresets={savedPresets}
      currentSelectedPresetIndex={currentSelectedPresetIndex}
      onReset={onReset}
      onPressSave={onPressSave}
      scheduleCalendar={scheduleCalendar}
    />
  )
}
